import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

export function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function createNaturalDivisionTask({ type, pickData, explain }) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "dzielenie"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>
          Oblicz: <InlineMath math={`${data.a}:${data.b}`} />.
        </p>
      </div>
    );
  }

  function renderInput(_task, userAnswer, onChange, disabled) {
    return (
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="wynik"
        placeholder="np. 14"
      />
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    const parsed = userAnswer.parsed;
    return Number.isFinite(parsed) && parsed === data.result;
  }

  function renderAnswer({ data }) {
    return <InlineMath math={`${data.result}`} />;
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
      return <i>(brak odpowiedzi)</i>;
    }
    return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
  }

  function renderExplanation({ data }) {
    return explain(data);
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  };
}

export function createNaturalDivisionRemainderTask({ type, pickData, explain }) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "dzielenie_z_reszta"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>
          Oblicz: <InlineMath math={`${data.a}:${data.b}`} />. Podaj wynik i resztę.
        </p>
      </div>
    );
  }

  function renderInput(_task, userAnswer = {}, onChange, disabled) {
    const currentAnswer = userAnswer || {};
    const update = (key) => (value) => onChange({ ...currentAnswer, [key]: value });
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <FractionInput
          value={currentAnswer.quotient}
          onChange={update("quotient")}
          disabled={disabled}
          label="wynik"
          placeholder="np. 7"
        />
        <FractionInput
          value={currentAnswer.remainder}
          onChange={update("remainder")}
          disabled={disabled}
          label="reszta"
          placeholder="np. 2"
        />
      </div>
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    const quotient = userAnswer.quotient?.parsed;
    const remainder = userAnswer.remainder?.parsed;
    return quotient === data.quotient && remainder === data.remainder;
  }

  function renderAnswer({ data }) {
    return (
      <>
        <InlineMath math={`${data.quotient}`} /> reszty <InlineMath math={`${data.remainder}`} />
      </>
    );
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
    const quotient = userAnswer.quotient?.raw;
    const remainder = userAnswer.remainder?.raw;
    if (!quotient && !remainder) return <i>(brak odpowiedzi)</i>;
    return (
      <>
        {quotient ? <InlineMath math={formatLatexPreview(quotient)} /> : <i>?</i>} reszty{" "}
        {remainder ? <InlineMath math={formatLatexPreview(remainder)} /> : <i>?</i>}
      </>
    );
  }

  function renderExplanation({ data }) {
    return explain(data);
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  };
}

export function tensOnesSplitExplanation(data) {
  const tens = Math.floor(data.a / 10) * 10;
  const ones = data.a % 10;
  return (
    <div style={{ textAlign: "left" }}>
      <p>Rozbijamy liczbę dzieloną na dziesiątki i jedności.</p>
      <p>
        <InlineMath math={`${data.a}=${tens}+${ones}`} />
      </p>
      <p>
        <InlineMath math={`${data.a}:${data.b}=(${tens}:${data.b})+(${ones}:${data.b})`} />
      </p>
      <p>
        <InlineMath math={`${tens / data.b}+${ones / data.b}=${data.result}`} />
      </p>
    </div>
  );
}

export function tableDivisionExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Dzielenie możemy sprawdzać za pomocą mnożenia.</p>
      <p>
        Szukamy liczby, która pomnożona przez <InlineMath math={`${data.b}`} /> daje{" "}
        <InlineMath math={`${data.a}`} />.
      </p>
      <p>
        <InlineMath math={`${data.result}\\cdot ${data.b}=${data.a}`} />, więc{" "}
        <InlineMath math={`${data.a}:${data.b}=${data.result}`} />.
      </p>
    </div>
  );
}

export function quotientSplitExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Rozbijamy liczbę dzieloną na takie części, które łatwo podzielić.</p>
      <p>
        <InlineMath math={`${data.a}=${data.firstPart}+${data.secondPart}`} />
      </p>
      <p>
        <InlineMath math={`${data.a}:${data.b}=(${data.firstPart}:${data.b})+(${data.secondPart}:${data.b})`} />
      </p>
      <p>
        <InlineMath math={`${data.firstQuotient}+${data.secondQuotient}=${data.result}`} />
      </p>
    </div>
  );
}

export function largerSplitExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Przy większej liczbie dzielonej wybieramy wygodną dużą część i resztę.</p>
      <p>
        <InlineMath math={`${data.a}=${data.firstPart}+${data.secondPart}`} />
      </p>
      <p>
        <InlineMath math={`${data.firstPart}:${data.b}=${data.firstQuotient}`} />,{" "}
        <InlineMath math={`${data.secondPart}:${data.b}=${data.secondQuotient}`} />.
      </p>
      <p>
        <InlineMath math={`${data.firstQuotient}+${data.secondQuotient}=${data.result}`} />
      </p>
    </div>
  );
}

export function zerosDivisionExplanation(data) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>Najpierw skracamy wspólne zera w dzielnej i dzielniku.</p>
      <p>
        <InlineMath math={`${data.a}:${data.b}=${data.reducedA}:${data.reducedB}`} />
      </p>
      <p>
        Teraz korzystamy z mnożenia:{" "}
        <InlineMath math={`${data.result}\\cdot ${data.reducedB}=${data.reducedA}`} />.
      </p>
      <p>
        <InlineMath math={`${data.a}:${data.b}=${data.result}`} />
      </p>
    </div>
  );
}

export function remainderDivisionExplanation(data) {
  const nextQuotient = data.quotient + 1;
  const nextProduct = nextQuotient * data.b;

  return (
    <div style={{ textAlign: "left" }}>
      <p>Sprawdzamy, ile pełnych razy dzielnik mieści się w liczbie dzielonej.</p>
      <p>
        Mieści się <InlineMath math={`${data.quotient}`} /> razy, bo{" "}
        <InlineMath math={`${data.quotient}\\cdot ${data.b}=${data.product}`} />.
      </p>
      <p>
        Kolejna grupa już się nie mieści:{" "}
        <InlineMath math={`${nextQuotient}\\cdot ${data.b}=${nextProduct}`} />, a{" "}
        <InlineMath math={`${nextProduct}>${data.a}`} />.
      </p>
      <p>
        Zostaje{" "}
        <InlineMath math={`${data.a}-${data.product}=${data.remainder}`} />, więc reszta to{" "}
        <InlineMath math={`${data.remainder}`} />.
      </p>
      <p>
        <InlineMath math={`${data.a}:${data.b}=${data.quotient}`} /> reszty{" "}
        <InlineMath math={`${data.remainder}`} />.
      </p>
      <p>
        Reszta jest mniejsza od dzielnika:{" "}
        <InlineMath math={`${data.remainder}<${data.b}`} />.
      </p>
    </div>
  );
}
